import { BoardTransition, Project } from "./types";

// Pure, edge-only transition checks for the UI. They mirror the backend's edge rule (the source of
// truth, which also evaluates conditions/validators) so the UI can avoid offering — or attempting —
// moves with no transition. Empty graph = unrestricted.

// Whether a from→to move has an allowed edge (ignores conditions/validators — those are evaluated
// server-side and surfaced as a rejection toast). Same status (reorder) is always allowed.
export function isEdgeAllowed(project: Project | undefined, fromStatusId: string, toStatusId: string): boolean {
  if (fromStatusId === toStatusId) return true;
  const transitions: BoardTransition[] = project?.transitions ?? [];
  if (transitions.length === 0) return true;
  return transitions.some(
    (t: BoardTransition) => t.toStatusId === toStatusId && (t.fromStatusId == null || t.fromStatusId === fromStatusId)
  );
}

// The transitions available FROM a status — the named actions a workflow offers (JIRA-style), in
// definition order, excluding self-moves. Empty when the project is unrestricted (no graph) or the
// status is a dead end; callers fall back to listing statuses when there's no workflow.
export function availableTransitions(project: Project | undefined, fromStatusId: string): BoardTransition[] {
  const transitions: BoardTransition[] = project?.transitions ?? [];
  return transitions.filter(
    (t: BoardTransition) => (t.fromStatusId == null || t.fromStatusId === fromStatusId) && t.toStatusId !== fromStatusId
  );
}

// The status ids an issue at `fromStatusId` may move to (plus itself). Returns null when the project
// is unrestricted (no transitions) — callers treat null as "all targets allowed".
export function allowedTargetStatusIds(project: Project | undefined, fromStatusId: string): Set<string> | null {
  const transitions: BoardTransition[] = project?.transitions ?? [];
  if (transitions.length === 0) return null;
  const allowed: Set<string> = new Set<string>([fromStatusId]);
  for (const t of transitions) {
    if (t.fromStatusId == null || t.fromStatusId === fromStatusId) allowed.add(t.toStatusId);
  }
  return allowed;
}
